import React, { useEffect, useRef, useState } from 'react';
import { PdfBlock, PdfHeader, PdfPreview, PdfFallback, FileIcon, FileMeta } from './styles';
import { DocIcon } from './icons';

/**
 * A PDF message: file card + an inline preview of the first page.
 *
 * The preview is mounted LAZILY (IntersectionObserver) — a thread can hold many
 * PDFs and each embed spins up the browser's PDF plugin, so mounting them all on
 * render makes scrolling crawl. Once shown it stays mounted, to avoid re-fetching
 * while scrolling back and forth.
 *
 * `#toolbar=0&navpanes=0&scrollbar=0` hides the built-in viewer chrome (the
 * Drive/download/print bar) in Chrome and Edge. Firefox/Safari ignore it and show
 * their own — harmless, just less clean.
 */
const PdfBubbleV2 = ({ url, name, owned, onOpen }) => {
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (visible || !ref.current) return undefined;
    // No IntersectionObserver (older browsers / jsdom) — just show it.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const open = () => {
    if (onOpen) onOpen(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <PdfBlock owned={owned}>
      <PdfHeader href={url} target="_blank" rel="noopener noreferrer">
        <FileIcon>
          <DocIcon size={20} />
        </FileIcon>
        <FileMeta>
          <div className="name">{name || 'Open file'}</div>
          <div className="sub">PDF document</div>
        </FileMeta>
      </PdfHeader>
      <PdfPreview ref={ref} onClick={open} title={name || 'Open PDF'}>
        {visible && !failed ? (
          <iframe
            src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            title={name || 'PDF preview'}
            loading="lazy"
            onError={() => setFailed(true)}
          />
        ) : (
          <PdfFallback>{failed ? 'Preview unavailable — tap to open' : 'Loading preview…'}</PdfFallback>
        )}
      </PdfPreview>
    </PdfBlock>
  );
};

export default PdfBubbleV2;
